import {
  addMoney2,
  allocateMoqFreightToUnit,
  centsToYuan,
  computeServiceFeeUnitFromExFactory,
  computeUntaxedSaleUnitFromExFactory,
  multiplyMoneyByRate,
  roundMoney2,
  yuanToCents,
} from './money';
import {
  resolveLineFreightTotal,
  normalizeFreightTiers,
  legacyFreightToTiers,
  type FreightTier,
} from './freightTiers';

export {
  addMoney2,
  allocateMoqFreightToUnit,
  centsToYuan,
  computeServiceFeeUnitFromExFactory,
  computeUntaxedSaleUnitFromExFactory,
  multiplyMoneyByRate,
  roundMoney2,
  yuanToCents,
} from './money';
export { formatFreightTierSummary, legacyFreightToTiers } from './freightTiers';
export { resolveLineFreightTotal, normalizeFreightTiers, type FreightTier } from './freightTiers';

/** @deprecated 使用 roundMoney2 */
export const floorMoney2 = roundMoney2;

export function normalizeTaxRate(input: unknown): number {
  if (input == null || input === '') return 0;
  const n = Number(input);
  if (!Number.isFinite(n) || n < 0) return 0;
  if (n > 1) return n / 100;
  if (n < 1) return n;
  return 0.01;
}

/** @deprecated 等价于 resolveLineFreightTotal / qty */
export function resolveFreightUnit(
  freightFee: unknown,
  freeShippingMinQty: unknown,
  orderQuantity: unknown,
): number {
  const qty = Math.max(1, Number(orderQuantity ?? 1));
  const total = resolveLineFreightTotal({
    freightFee,
    freeShippingMinQty,
    quantity: qty,
  });
  return qty > 0 ? roundMoney2(total / qty) : 0;
}

export type OrderLineAmounts = {
  untaxedSaleUnit: number;
  saleSubtotal: number;
  freightUnit: number;
  freightTotal: number;
  taxRate: number;
  taxAmount: number;
  lineTotal: number;
};

export function computeOrderLineAmounts(input: {
  untaxedSaleUnit: number;
  quantity: number;
  freightFee?: unknown;
  freeShippingMinQty?: unknown;
  freightTiers?: unknown;
  taxRate: unknown;
}): OrderLineAmounts {
  const qty = Math.max(1, Number(input.quantity ?? 1));
  const unit = roundMoney2(Number(input.untaxedSaleUnit));
  const saleSubtotal = centsToYuan(yuanToCents(unit) * qty);
  const freightTotal = roundMoney2(
    resolveLineFreightTotal({
      freightTiers: input.freightTiers,
      freightFee: input.freightFee,
      freeShippingMinQty: input.freeShippingMinQty,
      quantity: qty,
    }),
  );
  const freightUnit = qty > 0 ? roundMoney2(freightTotal / qty) : 0;
  const taxRate = normalizeTaxRate(input.taxRate);
  const taxBase = addMoney2(saleSubtotal, freightTotal);
  const taxAmount = multiplyMoneyByRate(taxBase, taxRate);
  const lineTotal = addMoney2(taxBase, taxAmount);
  return {
    untaxedSaleUnit: unit,
    saleSubtotal,
    freightUnit,
    freightTotal,
    taxRate,
    taxAmount,
    lineTotal,
  };
}

export type MallSalePriceBreakdown = {
  exFactory: number;
  serviceFee: number;
  untaxedSaleUnit: number;
  freight: number;
  freightTotal: number;
  preTaxSubtotal: number;
  tax: number;
  sale: number;
};

export function computeUntaxedSaleUnit(
  exFactoryPrice: number,
  markupPercent: unknown,
  estimatedFreightMoq: unknown = 0,
  minOrderQty: unknown = 1,
): number {
  const ex = Number(exFactoryPrice);
  const pct =
    markupPercent === null || markupPercent === undefined || markupPercent === ''
      ? NaN
      : Number(markupPercent);
  const m = Number.isFinite(pct) && pct >= 0 ? pct / 100 : 0;
  const exSafe = Number.isFinite(ex) && ex > 0 ? ex : 0;
  if (exSafe <= 0) return 0;
  const freightUnit = allocateMoqFreightToUnit(
    Number(estimatedFreightMoq) || 0,
    Number(minOrderQty) || 1,
  );
  return computeUntaxedSaleUnitFromExFactory(exSafe, m, freightUnit);
}

/** 未税技术服务费单价（出厂价×费率 + 预估运费/起订量） */
export function computeServiceFeeUnit(
  exFactoryPrice: number,
  markupPercent: unknown,
  estimatedFreightMoq: unknown = 0,
  minOrderQty: unknown = 1,
): number {
  const ex = Number(exFactoryPrice);
  const pct =
    markupPercent === null || markupPercent === undefined || markupPercent === ''
      ? NaN
      : Number(markupPercent);
  const m = Number.isFinite(pct) && pct >= 0 ? pct / 100 : 0;
  const exSafe = Number.isFinite(ex) && ex > 0 ? ex : 0;
  if (exSafe <= 0) return 0;
  const freightUnit = allocateMoqFreightToUnit(
    Number(estimatedFreightMoq) || 0,
    Number(minOrderQty) || 1,
  );
  return computeServiceFeeUnitFromExFactory(exSafe, m, freightUnit);
}

export function computeMallSalePriceBreakdown(
  exFactoryPrice: number,
  markupPercent: unknown,
  taxRate: unknown,
  freightFee = 0,
  quantity = 1,
  freeShippingMinQty?: unknown,
  freightTiers?: unknown,
): MallSalePriceBreakdown {
  const ex = Number(exFactoryPrice);
  const untaxedSaleUnit = computeUntaxedSaleUnit(ex, markupPercent);
  const qty = Math.max(1, Number(quantity ?? 1));
  const amounts = computeOrderLineAmounts({
    untaxedSaleUnit,
    quantity: qty,
    freightFee,
    freeShippingMinQty,
    freightTiers,
    taxRate,
  });
  const pct =
    markupPercent === null || markupPercent === undefined || markupPercent === ''
      ? NaN
      : Number(markupPercent);
  const m = Number.isFinite(pct) && pct >= 0 ? pct / 100 : 0;
  const exSafe = Number.isFinite(ex) && ex > 0 ? ex : 0;
  return {
    exFactory: exSafe,
    serviceFee: multiplyMoneyByRate(exSafe, m),
    untaxedSaleUnit,
    freight: amounts.freightUnit,
    freightTotal: amounts.freightTotal,
    preTaxSubtotal: addMoney2(amounts.saleSubtotal, amounts.freightTotal),
    tax: amounts.taxAmount,
    sale: qty > 0 ? roundMoney2(amounts.lineTotal / qty) : amounts.lineTotal,
  };
}

export function computeUntaxedSalePrice(
  exFactoryPrice: number,
  markupPercent: unknown,
  estimatedFreightMoq: unknown = 0,
  minOrderQty: unknown = 1,
): string {
  const ex = Number(exFactoryPrice);
  if (!Number.isFinite(ex) || ex <= 0) return '—';
  const pct =
    markupPercent === null || markupPercent === undefined || markupPercent === ''
      ? NaN
      : Number(markupPercent);
  if (!Number.isFinite(pct) || pct < 0) return '—';
  return computeUntaxedSaleUnit(ex, pct, estimatedFreightMoq, minOrderQty).toFixed(2);
}

export function computeMallSalePrice(
  exFactoryPrice: number,
  markupPercent: unknown,
  taxRate: unknown,
  freightFee = 0,
  quantity = 1,
  freeShippingMinQty?: unknown,
  freightTiers?: unknown,
): string {
  const ex = Number(exFactoryPrice);
  if (!Number.isFinite(ex) || ex <= 0) return '—';
  const b = computeMallSalePriceBreakdown(
    ex,
    markupPercent,
    taxRate,
    freightFee,
    quantity,
    freeShippingMinQty,
    freightTiers,
  );
  return b.sale.toFixed(2);
}

export function formatTaxRateLabel(rate: unknown): string {
  if (rate == null || rate === '') return '未配置';
  const n = normalizeTaxRate(rate);
  if (!n) return '0%';
  return `${(n * 100).toFixed(n * 100 % 1 === 0 ? 0 : 1)}%`;
}

export function taxRateToInputPercent(rate: unknown): number | undefined {
  if (rate == null || rate === '') return undefined;
  const n = Number(rate);
  if (!Number.isFinite(n)) return undefined;
  return n > 0 && n < 1 ? n * 100 : n;
}

export type VatInvoiceType = 'SPECIAL' | 'REGULAR';

/**
 * 无商品/供应商上架开票类型时的历史兜底（按专票）。
 * 优先级：商品 vat_invoice_type > 供应商 default > 专票。
 */
export const VAT_INVOICE_SPECIAL_ONLY = true;

export const DEFAULT_VAT_INVOICE_TYPE: VatInvoiceType = 'SPECIAL';

export function parseVatInvoiceType(type: unknown): VatInvoiceType | null {
  const s = String(type || '')
    .trim()
    .toUpperCase();
  if (s === 'SPECIAL' || s === 'REGULAR') return s;
  return null;
}

/** 上架开票：商品覆盖 > 供应商默认 > 专票 */
export function resolveListingVatInvoiceType(product?: {
  vat_invoice_type?: unknown;
  supplier_default_vat_invoice_type?: unknown;
} | null): VatInvoiceType {
  return (
    parseVatInvoiceType(product?.vat_invoice_type) ??
    parseVatInvoiceType(product?.supplier_default_vat_invoice_type) ??
    DEFAULT_VAT_INVOICE_TYPE
  );
}

export const VAT_INVOICE_TYPE_OPTIONS: { value: VatInvoiceType; label: string }[] = [
  { value: 'SPECIAL', label: '增值税专用发票（专票）' },
  { value: 'REGULAR', label: '增值税普通发票（普票）' },
];

/** 采购端实际生效的开票类型（优先锁定商品级，其次供应商级） */
export function resolveEffectiveVatInvoiceType(
  type: VatInvoiceType | null | undefined,
  product?: {
    vat_invoice_type?: unknown;
    supplier_default_vat_invoice_type?: unknown;
  } | null,
): VatInvoiceType | null {
  if (product) {
    return resolveListingVatInvoiceType(product);
  }
  if (VAT_INVOICE_SPECIAL_ONLY) return DEFAULT_VAT_INVOICE_TYPE;
  return parseVatInvoiceType(type);
}

export function formatVatInvoiceTypeLabel(type: unknown): string {
  const s = String(type || '')
    .trim()
    .toUpperCase();
  if (s === 'SPECIAL') return '增值税专用发票（专票）';
  if (s === 'REGULAR') return '增值税普通发票（普票）';
  return '—';
}

/** 按采购方选择的开票类型取商品适用税率（百分数或小数均可，返回百分数语义由 normalizeTaxRate 处理） */
export function resolveProductTaxRateForInvoice(
  product: {
    tax_rate?: unknown;
    tax_rate_special?: unknown;
    tax_rate_regular?: unknown;
  },
  invoiceType: unknown,
): number | null {
  const t = parseVatInvoiceType(invoiceType);
  if (t === 'SPECIAL') {
    const v = product.tax_rate_special ?? product.tax_rate;
    return v == null || v === '' ? null : Number(v);
  }
  if (t === 'REGULAR') {
    const v = product.tax_rate_regular;
    return v == null || v === '' ? null : Number(v);
  }
  return null;
}

/** 采购端开票选项：仅展示商品上架锁定的类型（带税率） */
export function vatInvoiceOptionsWithRates(product: {
  vat_invoice_type?: unknown;
  supplier_default_vat_invoice_type?: unknown;
  tax_rate?: unknown;
  tax_rate_special?: unknown;
  tax_rate_regular?: unknown;
}): { value: VatInvoiceType; label: string }[] {
  const listing = resolveListingVatInvoiceType(product);
  const special = formatTaxRateLabel(product.tax_rate_special ?? product.tax_rate);
  const regular = formatTaxRateLabel(product.tax_rate_regular);
  const options: { value: VatInvoiceType; label: string }[] = [
    { value: 'SPECIAL', label: `专票（税率 ${special}）` },
    { value: 'REGULAR', label: `普票（税率 ${regular}）` },
  ];
  return options.filter((opt) => opt.value === listing);
}

export function resolveProductUntaxedUnit(product: {
  base_price?: number | string | null;
  ex_factory_price?: number | string | null;
  has_variants?: boolean;
  variants?: Array<{
    id: number;
    base_price?: number | string | null;
    ex_factory_price?: number | string | null;
    is_active?: boolean;
  }> | null;
}, variantId?: number | null): number {
  if (product.has_variants && variantId != null) {
    const v = product.variants?.find((x) => x.id === variantId);
    if (v) return Number(v.base_price || 0);
  }
  return Number(product.base_price || 0);
}

/** 平台审价预览：按出厂价 + 技术服务费费率 + 预估运费展示未税销售单价 */
export function resolveAuditPreviewUntaxedUnit(
  product: {
    base_price?: number | string | null;
    ex_factory_price?: number | string | null;
    min_order_quantity?: number | null;
    has_variants?: boolean;
    variants?: Array<{
      id: number;
      base_price?: number | string | null;
      ex_factory_price?: number | string | null;
      min_order_quantity?: number | null;
      is_active?: boolean;
    }> | null;
  },
  variantId: number | null | undefined,
  serviceFeeRatePercent: number | undefined,
  estimatedFreightMoq: number | undefined = 0,
): number {
  const pct = serviceFeeRatePercent;
  const freightMoq = Number(estimatedFreightMoq) || 0;
  if (product.has_variants && variantId != null) {
    const v = product.variants?.find((x) => x.id === variantId);
    const ex = Number(v?.ex_factory_price ?? 0);
    const moq = Math.max(1, Number(v?.min_order_quantity ?? product.min_order_quantity ?? 1));
    if (ex > 0 && pct != null && Number.isFinite(pct)) {
      return computeUntaxedSaleUnit(ex, pct, freightMoq, moq);
    }
    return Number(v?.base_price || 0);
  }
  const moq = Math.max(1, Number(product.min_order_quantity ?? 1));
  const ex = Number(product.ex_factory_price ?? 0);
  if (ex > 0 && pct != null && Number.isFinite(pct)) {
    return computeUntaxedSaleUnit(ex, pct, freightMoq, moq);
  }
  return Number(product.base_price || 0);
}

/** 商品级运费档位（多规格共用；忽略规格级 freight_tiers） */
export function resolveLineFreightTiers(
  product: {
    freight_tiers?: FreightTier[] | null;
    freight_fee?: number | string | null;
    free_shipping_min_qty?: number | null;
    min_order_quantity?: number | null;
  },
  _variant?: { freight_tiers?: FreightTier[] | null; freight_fee?: number | string | null } | null,
): FreightTier[] {
  const productTiers = normalizeFreightTiers(product.freight_tiers);
  if (productTiers.length) {
    return productTiers.map((t) => ({
      min_qty: t.min_qty,
      max_qty: t.max_qty,
      freight_amount: t.freight_amount,
    }));
  }
  return legacyFreightToTiers(
    product.freight_fee,
    product.free_shipping_min_qty,
    product.min_order_quantity ?? 1,
  );
}

function allocateMoneyByWeight(weights: number[], total: number): number[] {
  const sum = weights.reduce((acc, w) => acc + w, 0);
  if (total <= 0 || sum <= 0) return weights.map(() => 0);
  let allocated = 0;
  return weights.map((weight, index) => {
    if (index === weights.length - 1) return roundMoney2(total - allocated);
    const share = roundMoney2((total * weight) / sum);
    allocated += share;
    return share;
  });
}

export type ProductGroupOrderAmounts = {
  totalQty: number;
  saleSubtotal: number;
  freightTotal: number;
  taxAmount: number;
  lineTotal: number;
  perLine: OrderLineAmounts[];
};

/** 同一商品多行（多规格）按采购数量总和计一次运费，再按未税小计比例分摊到各行 */
export function computeProductGroupOrderAmounts(input: {
  lines: Array<{ untaxedSaleUnit: number; quantity: number }>;
  freightTiers?: unknown;
  freightFee?: unknown;
  freeShippingMinQty?: unknown;
  taxRate: unknown;
}): ProductGroupOrderAmounts {
  const lines = input.lines.filter((line) => Math.floor(Number(line.quantity ?? 0)) > 0);
  if (!lines.length) {
    return {
      totalQty: 0,
      saleSubtotal: 0,
      freightTotal: 0,
      taxAmount: 0,
      lineTotal: 0,
      perLine: [],
    };
  }

  const taxRate = normalizeTaxRate(input.taxRate);
  const lineSubtotals = lines.map((line) => {
    const qty = Math.max(1, Math.floor(Number(line.quantity ?? 1)));
    const unit = roundMoney2(Number(line.untaxedSaleUnit));
    return {
      unit,
      qty,
      saleSubtotal: centsToYuan(yuanToCents(unit) * qty),
    };
  });
  const totalQty = lineSubtotals.reduce((sum, line) => sum + line.qty, 0);
  const saleSubtotal = lineSubtotals.reduce((sum, line) => sum + line.saleSubtotal, 0);
  const freightTotal = roundMoney2(
    resolveLineFreightTotal({
      freightTiers: input.freightTiers,
      freightFee: input.freightFee,
      freeShippingMinQty: input.freeShippingMinQty,
      quantity: totalQty,
    }),
  );
  const freightShares = allocateMoneyByWeight(
    lineSubtotals.map((line) => line.saleSubtotal),
    freightTotal,
  );
  const perLine = lineSubtotals.map((line, index) => {
    const lineFreight = freightShares[index] ?? 0;
    const freightUnit = line.qty > 0 ? roundMoney2(lineFreight / line.qty) : 0;
    const taxBase = addMoney2(line.saleSubtotal, lineFreight);
    const taxAmount = multiplyMoneyByRate(taxBase, taxRate);
    const lineTotal = addMoney2(taxBase, taxAmount);
    return {
      untaxedSaleUnit: line.unit,
      saleSubtotal: line.saleSubtotal,
      freightUnit,
      freightTotal: lineFreight,
      taxRate,
      taxAmount,
      lineTotal,
    };
  });
  const taxAmount = perLine.reduce((sum, line) => sum + line.taxAmount, 0);
  const lineTotal = perLine.reduce((sum, line) => sum + line.lineTotal, 0);
  return {
    totalQty,
    saleSubtotal,
    freightTotal,
    taxAmount,
    lineTotal,
    perLine,
  };
}

/** @deprecated 请用 resolveLineFreightTiers + computeOrderLineAmounts */
export function resolveLineFreightFee(
  product: { freight_fee?: number | string | null; free_shipping_min_qty?: number | null },
  variant?: { freight_fee?: number | string | null } | null,
): number {
  return Math.max(0, Number(variant?.freight_fee ?? product.freight_fee ?? 0));
}
